import { useMemo } from 'react';
import { CalendarX, Clock, Plus, Tag, Ticket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MoneyValue } from './MoneyValue';
import { useAvailability } from '@/hooks/queries';
import { summarizeAvailability, type UnavailableReason } from '@/lib/slotAvailability';
import { number as formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { shortBadge } from '@/lib/promoBadge';
import type { CatalogListPromo, Product } from '@/types/product';

interface ProductCardProps {
  product: Product;
  onSelect: (product: Product) => void;
  /** Stagger index for the load reveal. */
  index?: number;
  /** Date-resolved price for the visit date; overrides the base display price. */
  resolvedPrice?: number;
  /**
   * What checkout charges for ONE such ticket on the visit date
   * (`GET /product/catalog`), or null when no promo applies to a lone ticket.
   * Display only: the cart keeps `resolvedPrice`; the quote applies the promo.
   */
  promoPrice?: number | null;
  /** The simple promos a live campaign gives this ticket that day, checkout's pick first. */
  promos?: CatalogListPromo[];
  /** Visit date (YYYY-MM-DD) — drives slot-based capacity + expiry. */
  visitDate: string;
  /** The visit date is operator-blocked — the card is locked with a "Tutup" stamp. */
  blocked?: boolean;
}

/** Below this many tickets left, the capacity pill turns urgent (gold). */
const LOW_STOCK_THRESHOLD = 10;

const WATERMARK_LABEL: Record<UnavailableReason, string> = {
  full: 'Habis',
  expired: 'Waktu Habis',
  none: 'Tidak Tersedia',
};

export function ProductCard({
  product,
  onSelect,
  index = 0,
  resolvedPrice,
  promoPrice = null,
  promos = [],
  visitDate,
  blocked = false,
}: ProductCardProps) {
  // Main entry tickets are slot-based: capacity + expiry come from the day's
  // schedule, not a stock counter. Non-slot tickets fall back to stock/soldOut.
  const slotBased = product.requiresSlot === true;
  const avail = useAvailability(product.id, visitDate, slotBased);

  const summary = useMemo(() => {
    if (!slotBased) {
      const stock = product.stock;
      const full = product.soldOut === true || stock === 0;
      return {
        state: full ? ('unavailable' as const) : ('available' as const),
        reason: full ? ('full' as UnavailableReason) : undefined,
        remaining: typeof stock === 'number' ? stock : null,
      };
    }
    // Never block on a fetch error; keep the ticket sellable and hide capacity.
    if (avail.isError) return { state: 'available' as const, remaining: null };
    // Slots still loading — leave the card enabled, no watermark yet.
    if (avail.isLoading || !avail.data) return null;
    return summarizeAvailability(avail.data.slots, { visitDate });
  }, [slotBased, product.stock, product.soldOut, avail.isError, avail.isLoading, avail.data, visitDate]);

  const unavailable = blocked || summary?.state === 'unavailable';
  const reason = summary?.reason;
  const remaining = summary?.state === 'available' ? summary.remaining : null;
  const lowStock = typeof remaining === 'number' && remaining <= LOW_STOCK_THRESHOLD;

  // The date-aware price before promos (falls back to the weekday base when the
  // resolver hasn't returned yet).
  const displayPrice = typeof resolvedPrice === 'number' ? resolvedPrice : product.price;
  // Only the backend's catalog counts: `Product.discountedPrice` is a static
  // column nothing keeps up to date, so it could show a discount the checkout
  // would never give.
  const discounted = typeof promoPrice === 'number' && promoPrice < displayPrice;
  // The badge names a promo checkout actually applies to this ticket alone.
  const applying = promos.find((p) => p.appliesAtCheckout);
  const promoLabel = applying ? applying.badge || applying.label : null;
  const promoBadge = promoLabel ? shortBadge(promoLabel) : null;

  return (
    <button
      type="button"
      disabled={unavailable}
      onClick={() => onSelect(product)}
      style={{ animationDelay: `${Math.min(index, 16) * 28}ms` }}
      className={cn(
        'group relative flex animate-reveal-up flex-col overflow-hidden rounded-lg border-2 border-border bg-card text-left shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-pop',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        unavailable &&
          'cursor-not-allowed hover:translate-y-0 hover:border-border hover:shadow-sm',
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary">
        {product.picture?.url ? (
          <img
            src={product.picture.url}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-cream-100 to-cream-200 p-6">
            <img
              src="/logo-autopia-primary.png"
              alt=""
              aria-hidden
              className="max-h-12 w-auto max-w-[72%] object-contain opacity-25"
              loading="lazy"
            />
          </div>
        )}
        {product.requiresSlot && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-ink-900/70 px-2 py-0.5 text-[10px] font-semibold uppercase text-cream-50">
            <Clock className="h-3 w-3" /> Slot
          </span>
        )}
        {discounted && promoBadge && (
          <span className="absolute right-2 top-2" title={promoBadge !== promoLabel ? promoLabel ?? undefined : undefined}>
            <Badge variant="gold">{promoBadge}</Badge>
          </span>
        )}

        {/* Sold-out / expired watermark — hatched overlay + rotated stamp. */}
        {unavailable && (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[#211b17]/70">
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0 8px, rgba(255,255,255,0) 8px 16px)',
              }}
            />
            <span className="relative inline-flex -rotate-6 items-center gap-1.5 rounded-md border-2 border-cream-50 px-4 py-1 font-display text-lg font-bold uppercase tracking-widest text-cream-50">
              {blocked ? (
                <>
                  <CalendarX className="h-4 w-4" /> Tutup
                </>
              ) : (
                <>
                  {reason === 'expired' && <Clock className="h-4 w-4" />}
                  {WATERMARK_LABEL[reason ?? 'full']}
                </>
              )}
            </span>
          </div>
        )}

        {!unavailable && (
          <div className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100">
            <Plus className="h-5 w-5" />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-3">
        {product.category && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            {product.category}
          </span>
        )}
        <h3 className="line-clamp-2 font-sans text-sm font-semibold leading-snug text-ink-900">
          {product.name}
        </h3>
        {promos.length > 0 && <PromoChips promos={promos} />}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pt-1">
          <div className="flex items-baseline gap-2">
            {discounted ? (
              <>
                <MoneyValue value={promoPrice} emphasis className="text-base" />
                <MoneyValue value={displayPrice} strike className="text-xs" />
              </>
            ) : (
              <MoneyValue value={displayPrice} className="text-base" />
            )}
          </div>
          {/* Remaining capacity for the visit date — gold when stock runs low. */}
          {!unavailable && typeof remaining === 'number' && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                lowStock ? 'bg-[#e6b800]/20 text-gold-600' : 'bg-success/15 text-success',
              )}
            >
              <Ticket className="h-3 w-3" />
              Sisa {formatNumber(remaining)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * The one reason line worth adding to a tooltip. The others are already said
 * by the tooltip itself: a Buy-X-Get-Y names both tickets, and a minimum or a
 * first purchase is the tooltip body.
 */
const REASON_NOTE: Partial<Record<NonNullable<CatalogListPromo['reason']>, string>> = {
  NOT_COMBINABLE: 'Tidak digabung — berlaku promo yang lebih besar',
};

/**
 * The day's promos as chips, each with its own tooltip. A promo checkout
 * applies to this ticket is filled gold; one that needs more (another ticket,
 * a minimum, a first purchase) is dashed, so the cashier can tell at a glance
 * which one the price already reflects. Every promo gets a chip, so every
 * promo can be hovered on its own.
 *
 * Spans, not buttons: the whole card is the button that adds the ticket, and
 * a nested control would be invalid markup. The tooltips are therefore a
 * mouse affordance, which is what the counter PCs have.
 */
function PromoChips({ promos }: { promos: CatalogListPromo[] }) {
  return (
    <span data-testid="promo-chips" className="inline-flex flex-wrap items-center gap-1">
      <Tag className="h-3 w-3 text-gold-600" aria-hidden />
      {promos.map((p) => (
        <PromoChip key={p.discountId} promo={p} />
      ))}
    </span>
  );
}

function PromoChip({ promo }: { promo: CatalogListPromo }) {
  const note = promo.reason ? REASON_NOTE[promo.reason] : undefined;
  // The discount's name heads the tooltip so the cashier can tell the
  // customer which promo it is; skipped when it would only repeat the rule.
  const name = promo.name?.trim();
  const showName = Boolean(name) && name !== promo.tooltip.title;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={showName ? `${name}: ${promo.tooltip.title}` : promo.tooltip.title}
          className={cn(
            'rounded-full px-1.5 py-px text-[10px] font-semibold leading-tight',
            promo.appliesAtCheckout
              ? 'bg-gold-200 text-ink-900'
              : 'border border-dashed border-gold-600/60 text-gold-600',
          )}
        >
          {shortBadge(promo.label)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-xs space-y-0.5 py-2">
        {showName && <p className="font-semibold">{name}</p>}
        <p className={showName ? 'font-normal' : 'font-semibold'}>{promo.tooltip.title}</p>
        {promo.tooltip.body && <p className="font-normal text-cream-50/80">{promo.tooltip.body}</p>}
        {note && <p className="font-normal italic text-cream-50/60">{note}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
